import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Download, Filter, RotateCcw, Users, UserRound, Wallet, Boxes, TrendingUp } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const STATUSES = ["Active", "Applied", "Allotted", "Listed", "Lapsed", "Matured", "Surrendered", "Closed"];
const EMPTY = { date_from: "", date_to: "", branch: "", manager: "", product: "", potential: "", status: "", birthday: "", birthday_month: "" };

export default function Reports() {
  const { user, branch } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [products, setProducts] = useState([]);

  const buildParams = (f) => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) p[k] = v; });
    return p;
  };

  const load = useCallback((f) => {
    api.get("/analytics/report", { params: buildParams(f) }).then((r) => setData(r.data));
  }, []);

  useEffect(() => {
    load(EMPTY);
    api.get("/products").then((r) => setProducts(r.data.map((p) => ({ key: p.key, label: p.label }))));
    if (user.role === "super_admin") api.get("/branches").then((r) => setBranches(r.data));
    if (user.role !== "manager") api.get("/managers").then((r) => setManagers(r.data));
  }, [load, user.role]);

  const apply = () => { setApplied(filters); load(filters); };
  const reset = () => { setFilters(EMPTY); setApplied(EMPTY); load(EMPTY); };

  const exportFile = async (fmt) => {
    try {
      const res = await api.get("/analytics/export", { params: { fmt, ...buildParams(applied) }, responseType: "blob" });
      const ext = fmt === "xlsx" ? "xlsx" : fmt;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `wealthoo_report.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Export failed");
    }
  };

  if (!data) return <div className="text-muted-foreground py-20 text-center">Loading reports…</div>;
  const s = data.summary;

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader title="Reports" subtitle="Filterable business, product & onboarding analytics with exports">
        <div className="flex items-center gap-2" data-testid="report-export-buttons">
          <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx-button" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</button>
          <button onClick={() => exportFile("csv")} data-testid="export-csv-button" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><Download className="h-3.5 w-3.5" /> CSV</button>
          <button onClick={() => exportFile("pdf")} data-testid="export-pdf-button" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><FileText className="h-3.5 w-3.5" /> PDF</button>
        </div>
      </PageHeader>

      {/* Filters */}
      <div className="card-surface rounded-xl p-5" data-testid="report-filters">
        <div className="flex items-center gap-2 mb-4"><Filter className="h-4 w-4 text-primary" /><h3 className="font-display font-semibold">Filters</h3></div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <FField label="Start Date"><input type="date" value={filters.date_from} data-testid="filter-date-from" onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" /></FField>
          <FField label="End Date"><input type="date" value={filters.date_to} data-testid="filter-date-to" onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" /></FField>
          {user.role === "super_admin" && (
            <FField label="Branch">
              <Select value={filters.branch || "all"} onValueChange={(v) => setFilters({ ...filters, branch: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-branch" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </FField>
          )}
          {user.role !== "manager" && (
            <FField label="Manager">
              <Select value={filters.manager || "all"} onValueChange={(v) => setFilters({ ...filters, manager: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-manager" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Managers</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </FField>
          )}
          <FField label="Product">
            <Select value={filters.product || "all"} onValueChange={(v) => setFilters({ ...filters, product: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-product" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Products</SelectItem>{products.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </FField>
          <FField label="Potential">
            <Select value={filters.potential || "all"} onValueChange={(v) => setFilters({ ...filters, potential: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-potential" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any</SelectItem>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}★</SelectItem>)}</SelectContent>
            </Select>
          </FField>
          <FField label="Status">
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-status" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any</SelectItem>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
          </FField>
          <FField label="Birthday">
            <Select value={filters.birthday || "all"} onValueChange={(v) => setFilters({ ...filters, birthday: v === "all" ? "" : v, birthday_month: "" })}>
              <SelectTrigger data-testid="filter-birthday" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any</SelectItem><SelectItem value="today">Today</SelectItem><SelectItem value="upcoming">Upcoming (30d)</SelectItem></SelectContent>
            </Select>
          </FField>
          <FField label="Birthday Month">
            <Select value={filters.birthday_month || "all"} onValueChange={(v) => setFilters({ ...filters, birthday_month: v === "all" ? "" : v, birthday: "" })}>
              <SelectTrigger data-testid="filter-birthday-month" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any Month</SelectItem>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </FField>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={apply} data-testid="apply-filters-button" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors"><Filter className="h-4 w-4" /> Apply Filters</button>
          <button onClick={reset} data-testid="reset-filters-button" className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:border-primary/40 row-hover"><RotateCcw className="h-4 w-4" /> Reset</button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="report-summary">
        <SumCard label="Matched Clients" value={s.matched_clients} icon={Users} />
        <SumCard label="New Clients" value={s.new_clients} icon={UserRound} />
        <SumCard label="Total Business" value={formatINR(s.total_business)} icon={Wallet} />
        <SumCard label="Current Value" value={formatINR(s.total_current)} icon={TrendingUp} />
        <SumCard label="Products" value={s.product_count} icon={Boxes} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Product-wise Business">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.product_business.map((p) => ({ name: p.label, value: p.current, key: p.key }))} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => formatINR(v)} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => formatINR(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#3B82F6" cursor="pointer" onClick={(d) => d && d.key && navigate(`/clients?product=${d.key}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Monthly Business Trend">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.business_trend} margin={{ left: -10 }}>
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#94A3B8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => formatINR(v)} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => formatINR(v)} />
              <Bar dataKey="business" radius={[6, 6, 0, 0]} fill="#059669" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Onboarding */}
      <ChartCard title="Client Onboarding Trend">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.onboarding} margin={{ left: -10 }}>
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#94A3B8" }} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#D97706">
              {data.onboarding.map((_, i) => <Cell key={i} fill="#D97706" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Detailed table */}
      <div className="card-surface rounded-xl overflow-hidden" data-testid="report-table">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-display font-semibold">Business Records</h3>
          <span className="text-xs text-muted-foreground num">{data.records.length} rows</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Branch</th>
                <th className="text-left px-4 py-2.5">Manager</th><th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Product</th><th className="text-right px-4 py-2.5">Business</th>
                <th className="text-right px-4 py-2.5">Current</th><th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.records.slice(0, 400).map((r, i) => (
                <tr key={i} onClick={() => r.client_id && navigate(`/clients/${r.client_id}`)} data-testid={`report-row-${i}`} className="border-b border-border/40 last:border-0 cursor-pointer row-hover hover:bg-secondary">
                  <td className="px-4 py-2.5 num text-xs">{r.date}</td>
                  <td className="px-4 py-2.5">{r.branch}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{r.manager}</td>
                  <td className="px-4 py-2.5 font-medium">{r.client}</td>
                  <td className="px-4 py-2.5">{r.product}</td>
                  <td className="px-4 py-2.5 text-right num">{formatINR(r.business)}</td>
                  <td className="px-4 py-2.5 text-right num text-primary">{formatINR(r.current)}</td>
                  <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{r.status}</span></td>
                </tr>
              ))}
              {data.records.length === 0 && <tr><td colSpan={8} className="text-center py-14 text-muted-foreground">No records match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FField({ label, children }) {
  return <div><label className="text-xs font-medium text-muted-foreground">{label}</label>{children}</div>;
}
function SumCard({ label, value, icon: Icon }) {
  return (
    <div className="card-surface rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1.5 font-display text-xl font-bold num">{value}</div></div>
        <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Icon className="h-4 w-4" /></div>
      </div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="card-surface rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-primary" /><h3 className="font-display font-semibold">{title}</h3></div>
      {children}
    </div>
  );
}
